// Desktop (Electron) app version detection from the user-agent string.
//
// Electron UAs look like:
//   Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko)
//   omnia-practice-management/1.0.11 Chrome/120.0.6099.291 Electron/28.3.3 Safari/537.36
//
// The app version is the "<name>/<x.y.z>" token immediately before " Chrome/".
// We deliberately do NOT key on the product name — it varies across installs
// (omnia-practice-management / celary-legal / legatum-iuris) because of the
// two rebrands, while the appId stayed the same.
//
// Why this exists: the pop-out timer window's persisted-bounds slot
// ('timer' boundsKey) was restored verbatim by desktop <= 1.0.11 and could
// shrink to the 320x320 floor.  Desktop 1.0.12 fixes the restore math, so the
// renderer only opts into persisted bounds on >= 1.0.12 (see Layout.js
// spawnTimerWindow).

const ELECTRON_APP_VERSION_RE = /\S+\/(\d+)\.(\d+)\.(\d+)\s+Chrome\//

// Returns [major, minor, patch] or null when the UA is not an Electron build
// of this app (plain browser, empty, unparseable).
export function parseDesktopVersion (ua) {
  if (typeof ua !== 'string' || !ua) return null
  if (!/\bElectron\//.test(ua)) return null
  const m = ua.match(ELECTRON_APP_VERSION_RE)
  if (!m) return null
  return [Number(m[1]), Number(m[2]), Number(m[3])]
}

// Numeric (not lexical) semver compare of [maj, min, patch] tuples or
// "x.y.z" strings.  Returns -1 / 0 / 1.
export function compareVersions (a, b) {
  const toTuple = v => (Array.isArray(v) ? v : String(v).split('.')).map(n => Number(n) || 0)
  const x = toTuple(a)
  const y = toTuple(b)
  for (let i = 0; i < 3; i++) {
    const d = (x[i] || 0) - (y[i] || 0)
    if (d !== 0) return d > 0 ? 1 : -1
  }
  return 0
}

// True only when the UA is our desktop app AND its version >= min.
// Unparseable / browser UAs return false (fail toward the conservative path).
export function isDesktopAtLeast (min, ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '')) {
  const v = parseDesktopVersion(ua)
  if (!v) return false
  return compareVersions(v, min) >= 0
}

// Single source of truth for the pop-out timer window's open options, used by
// EVERY timer spawn path (Layout.spawnTimerWindow AND useTimerEventBridge,
// which handles spawns from inside popped-out TimeEntries / MatterDetail
// windows).  Keeping it in one place is what stops the two paths drifting —
// they previously disagreed (700 vs 720 tall, and the bridge sent no
// minWidth/minHeight at all).
//
// Size: 480 wide keeps Date/Rate + Activity/Billing Status two-column; 720
// tall fits the whole form (Matter, Attorney, Time+START, Date/Rate,
// Activity/Billing, Description, buttons) plus the Report-issue footer strip
// and the Windows title bar without scrolling.  The desktop app clamps it to
// the display's work area on small/scaled screens.
//
// boundsKey 'timer' (feedback #514) gives every timer window one persisted
// bounds slot so it reopens where the user last put it.  VERSION-GATED:
// desktop <= 1.0.11 restored that slot verbatim — no floor at the requested
// size, no DPI (scaleFactor) handling, saved even while minimized — so on
// scaled laptop displays the timer kept reopening at the 320x320 floor.
// 1.0.12 fixes the restore math.  On older desktops (or an unparseable UA) we
// omit boundsKey so the unfixed main process opens a fresh default-size window.
export const TIMER_WINDOW_MIN_DESKTOP = '1.0.12'
export function timerWindowOptions (ua) {
  const opts = { width: 480, height: 720, minWidth: 420, minHeight: 480 }
  // ua undefined → isDesktopAtLeast's default (navigator.userAgent) applies.
  if (isDesktopAtLeast(TIMER_WINDOW_MIN_DESKTOP, ua)) opts.boundsKey = 'timer'
  return opts
}
